import {
  KillChainCategory,
  StudyMissionCapabilityGapPriority,
  StudyMissionCapabilityGapStatus,
  type StudyMissionSelectDto,
} from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Modal } from '@components/core/modal.component';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import {
  useCreateMeiaStudyMission,
  useDeleteMeiaStudyMission,
  useGetMeiaMissionPurposeTemplates,
  useUpdateMeiaStudyMission,
} from '@queries/meia.query';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { type MeiaMissionFormValues, meiaMissionSchema } from '@schemas/meia.schema';
import { createFormInvalidHandler } from '@utils/form.util';
import { getStudyThreadKillChainLabel } from '@utils/meia-enum.util';
import { ChevronDownIcon, Trash2Icon } from 'lucide-react';
import { type FC, useState } from 'react';
import { MEIA_AUTOFILL_IGNORE_PROPS } from './meia-form-autofill.util';
import {
  getMeiaMissionAreaOfOperations,
  getMeiaMissionAssumptionsConstraints,
  getMeiaMissionAuthoritiesRoeEnvelope,
  getMeiaMissionBlueForceComposition,
  getMeiaMissionCommandersIntent,
  getMeiaMissionDesiredOutcome,
  getMeiaMissionName,
  getMeiaMissionTimeWindow,
} from './meia-mission-display.util';
import { type MeiaTemplateOption, MeiaTemplatePopover } from './meia-template-popover.component';

type MeiaMissionModalProps = {
  isOpen: boolean;
  mission?: StudyMissionSelectDto;
  onClose: () => void;
  onMissionCreated?: (missionId: string) => void;
  studyId: string;
};

const missionCapabilityGapPriorityOptions = [
  { label: 'High', value: StudyMissionCapabilityGapPriority.HIGH },
  { label: 'Medium', value: StudyMissionCapabilityGapPriority.MEDIUM },
  { label: 'Low', value: StudyMissionCapabilityGapPriority.LOW },
];

const missionCapabilityGapStatusOptions = [
  { label: 'Open', value: StudyMissionCapabilityGapStatus.OPEN },
  { label: 'Investigating', value: StudyMissionCapabilityGapStatus.INVESTIGATING },
  { label: 'Answered', value: StudyMissionCapabilityGapStatus.ANSWERED },
];

const missionTypeCategoryOptions = Object.values(KillChainCategory).map((value) => ({
  label: getStudyThreadKillChainLabel(value),
  value,
}));

const prototypeCapabilityGapTemplates: MeiaTemplateOption[] = [
  {
    id: 'm-015-a',
    name: 'What capability gaps prevent suppression of forward A2/AD fires within the 90-minute joint-fires window?',
  },
  {
    id: 'm-015-b',
    name: 'How does suppressed-SAM window duration affect SAG mission-kill probability?',
  },
  {
    id: 'm-014-a',
    name: 'What capability gaps prevent zero-impact perimeter defense against Group-3 UAS over 96 h?',
  },
  {
    id: 'm-014-b',
    name: 'How does upstream UAS C2 disruption translate to defeated-on-perimeter rate?',
  },
  {
    id: 'm-013-a',
    name: 'Is sustainment posture sufficient to hold 80% intermodal throughput for 21 days under contested conditions?',
  },
  {
    id: 'm-013-b',
    name: 'What C2 design requirements emerge from surviving 6-hour SATCOM blackouts?',
  },
  {
    id: 'm-012-a',
    name: 'What is the operational impact if cyber access TTL drops below 30 days?',
  },
  {
    id: 'm-010-a',
    name: 'Where does F2T2EA latency exceed the 12-minute TST decision window?',
  },
];

export const MeiaMissionModal: FC<MeiaMissionModalProps> = (props) => {
  const { isOpen, mission, onClose, onMissionCreated, studyId } = props;
  const isEdit = Boolean(mission);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const { isPending: isCreating, mutateAsync: createMission } = useCreateMeiaStudyMission();
  const { isPending: isUpdating, mutateAsync: updateMission } = useUpdateMeiaStudyMission();
  const { mutateAsync: deleteMission } = useDeleteMeiaStudyMission();
  const { data: missionPurposeTemplates } = useGetMeiaMissionPurposeTemplates();

  const resolvedTemplates = missionPurposeTemplates?.missionPurposeTemplates ?? [];

  const defaultValues: MeiaMissionFormValues = {
    capabilityGaps: (mission?.capabilityGaps ?? [])
      .toSorted((first, second) => first.position - second.position)
      .map((gap, index) => ({
        id: gap.id,
        position: index,
        priority: gap.priority,
        question: gap.question,
        sourcePromptRef: gap.sourcePromptRef ?? null,
        status: gap.status,
      })),
    missionContext: {
      areaOfOperations: getMeiaMissionAreaOfOperations(mission),
      assumptionsConstraints: getMeiaMissionAssumptionsConstraints(mission),
      authoritiesRoeEnvelope: getMeiaMissionAuthoritiesRoeEnvelope(mission),
      blueForceComposition: getMeiaMissionBlueForceComposition(mission),
      commandersIntent: getMeiaMissionCommandersIntent(mission),
      desiredMissionOutcome: getMeiaMissionDesiredOutcome(mission),
      timeWindow: getMeiaMissionTimeWindow(mission),
    },
    missionTypeCategory: mission?.missionTypeCategory ?? null,
    name: mission ? getMeiaMissionName(mission) : '',
  };

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      const validationResult = meiaMissionSchema.safeParse(value);
      if (!validationResult.success) {
        errorToast({ description: 'Please complete required mission fields.' });
        return;
      }

      const payload = {
        ...validationResult.data,
        capabilityGaps: validationResult.data.capabilityGaps.map((gap, index) => ({
          ...gap,
          position: index,
        })),
      };

      if (mission) {
        await updateMission({
          ...payload,
          missionId: mission.id,
          studyId,
        });
        successToast({ description: 'Mission updated.' });
      } else {
        const created = await createMission({ ...payload, studyId });
        onMissionCreated?.(created.id);
        successToast({ description: 'Mission created.' });
      }
      onClose();
    },
    onSubmitInvalid: createFormInvalidHandler<typeof defaultValues>(),
    validators: { onSubmit: meiaMissionSchema },
  });

  const handleConfirmDelete = async (): Promise<void> => {
    if (!mission) return;
    await deleteMission({ missionId: mission.id, studyId });
    successToast({ description: 'Mission deleted.' });
    onClose();
  };

  const handleTemplateSelect = (template: MeiaTemplateOption): void => {
    form.setFieldValue('name', template.name);
    form.setFieldValue('missionContext.areaOfOperations', template.missionContext?.areaOfOperations ?? '');
    form.setFieldValue('missionContext.assumptionsConstraints', template.missionContext?.assumptionsConstraints ?? '');
    form.setFieldValue('missionContext.authoritiesRoeEnvelope', template.missionContext?.authoritiesRoeEnvelope ?? '');
    form.setFieldValue('missionContext.blueForceComposition', template.missionContext?.blueForceComposition ?? '');
    form.setFieldValue('missionContext.commandersIntent', template.missionContext?.commandersIntent ?? '');
    form.setFieldValue(
      'missionContext.desiredMissionOutcome',
      template.missionContext?.desiredMissionOutcome ?? template.description ?? template.name,
    );
    form.setFieldValue('missionContext.timeWindow', template.missionContext?.timeWindow ?? '');
  };

  const isSaving = isCreating || isUpdating;

  return (
    <>
      <Modal
        centered
        footer={{
          leftButton: isEdit ? (
            <Button
              onClick={() => setIsConfirmingDelete(true)}
              type="button"
              variant="destructive"
            >
              <Trash2Icon className="size-4" />
              Delete
            </Button>
          ) : undefined,
          rightButton: {
            children: isSaving ? 'Saving...' : isEdit ? 'Save' : 'Add Mission',
            disabled: isSaving,
            onClick: () => void form.handleSubmit(),
          },
        }}
        header={{
          actions: (
            <MeiaTemplatePopover
              onSelect={handleTemplateSelect}
              templates={resolvedTemplates.map((template) => ({
                description: template.statementTemplate,
                id: template.id,
                missionContext: template.missionContext,
                name: template.name,
              }))}
            />
          ),
          title: isEdit ? 'Edit Mission' : 'Add Mission',
        }}
        isOpen={isOpen}
        onClose={onClose}
        width="640px"
      >
        <form
          {...MEIA_AUTOFILL_IGNORE_PROPS}
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <form.AppField name="name">
              {(field) => (
                <field.TextField
                  {...MEIA_AUTOFILL_IGNORE_PROPS}
                  label="Mission Name"
                  placeholder="Suppress forward A2/AD fires"
                  required
                />
              )}
            </form.AppField>
            <form.AppField name="missionContext.desiredMissionOutcome">
              {(field) => (
                <field.TextAreaField
                  label="Desired Mission Outcome (DMO)"
                  placeholder="Suppress long-range SAM coverage across 6 forward batteries for 90 minutes."
                  required
                  rows={3}
                />
              )}
            </form.AppField>
            <form.AppField name="missionContext.commandersIntent">
              {(field) => (
                <field.TextAreaField
                  label="Commander's Intent"
                  placeholder="Create a kinetic opening long enough for Maritime Strike to close on the SAG."
                  required
                  rows={3}
                />
              )}
            </form.AppField>
            <form.AppField name="missionTypeCategory">
              {(field) => (
                <field.SelectField
                  clearable
                  label="Mission Type"
                  options={missionTypeCategoryOptions}
                  placeholder="Select mission type"
                />
              )}
            </form.AppField>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <form.AppField name="missionContext.areaOfOperations">
                {(field) => (
                  <field.TextAreaField
                    label="Area of Operations"
                    placeholder="Adversary coastal SAM belt, 80 nm littoral band, first-island-chain south."
                    required
                    rows={3}
                  />
                )}
              </form.AppField>
              <form.AppField name="missionContext.timeWindow">
                {(field) => (
                  <field.TextAreaField
                    label="Time Window"
                    placeholder="T+0 through T+90 min, 2026-07-02 0300Z-0430Z."
                    required
                    rows={3}
                  />
                )}
              </form.AppField>
              <form.AppField name="missionContext.blueForceComposition">
                {(field) => (
                  <field.TextAreaField
                    label="Blue Force Composition"
                    placeholder="2x F-35A / 2x EA-18G package plus organic HARM and Maven BDA feed."
                    rows={3}
                  />
                )}
              </form.AppField>
              <form.AppField name="missionContext.authoritiesRoeEnvelope">
                {(field) => (
                  <field.TextAreaField
                    label="Authorities / ROE Envelope"
                    placeholder="Positive ID required; no cross-border action."
                    rows={3}
                  />
                )}
              </form.AppField>
            </div>
            <form.AppField name="missionContext.assumptionsConstraints">
              {(field) => (
                <field.TextAreaField
                  label="Assumptions & Constraints"
                  placeholder="IADS posture matches latest composite intel update; no mobilization spike inside window."
                  rows={6}
                />
              )}
            </form.AppField>
            <form.AppField
              mode="array"
              name="capabilityGaps"
            >
              {(gapArrayField) => {
                const capabilityGaps = gapArrayField.state.value ?? [];

                return (
                  <div className="flex flex-col gap-3">
                    <h3 className="text-sm font-semibold text-gray-900">Capability Gaps ({capabilityGaps.length})</h3>

                    {capabilityGaps.length === 0 && (
                      <p className="text-sm text-gray-500">
                        Add the missing capabilities as questions the mission must answer.
                      </p>
                    )}

                    {capabilityGaps.map((gap, gapIndex) => (
                      <div
                        className="rounded-md bg-gray-100 p-2"
                        key={gap.id ?? `capability-gap-${gapIndex}`}
                      >
                        <div className="rounded-md bg-white">
                          <AccordionPrimitive.Root
                            collapsible
                            defaultValue="open"
                            type="single"
                          >
                            <AccordionPrimitive.Item value="open">
                              <div className="flex items-center justify-between gap-2 px-3 py-2">
                                <AccordionPrimitive.Trigger className="group flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 transition-colors hover:text-gray-700">
                                  <ChevronDownIcon className="size-3 shrink-0 text-gray-400 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                                  GQ #{gapIndex + 1}
                                </AccordionPrimitive.Trigger>
                                <div className="flex items-center gap-2">
                                  <MeiaTemplatePopover
                                    onSelect={(template) => {
                                      form.setFieldValue(`capabilityGaps[${gapIndex}].question`, template.name);
                                      form.setFieldValue(`capabilityGaps[${gapIndex}].sourcePromptRef`, template.id);
                                    }}
                                    renderTemplate={(template) => (
                                      <span className="text-sm text-gray-700">{template.name}</span>
                                    )}
                                    templates={prototypeCapabilityGapTemplates}
                                  />
                                  <IconButton
                                    ariaLabel={`Remove capability gap ${gapIndex + 1}`}
                                    icon={<Trash2Icon />}
                                    onClick={() => gapArrayField.removeValue(gapIndex)}
                                    variant="destructive"
                                  />
                                </div>
                              </div>

                              <AccordionPrimitive.Content className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                                <div className="flex flex-col gap-3 p-3 pt-0">
                                  <form.AppField name={`capabilityGaps[${gapIndex}].question`}>
                                    {(field) => (
                                      <field.TextAreaField
                                        label="Capability Gap Question"
                                        placeholder="What capability is missing, underperforming, or unproven for this mission?"
                                        required
                                        rows={2}
                                      />
                                    )}
                                  </form.AppField>

                                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                    <form.AppField name={`capabilityGaps[${gapIndex}].priority`}>
                                      {(field) => (
                                        <field.SelectField
                                          label="Priority"
                                          options={missionCapabilityGapPriorityOptions}
                                        />
                                      )}
                                    </form.AppField>
                                    <form.AppField name={`capabilityGaps[${gapIndex}].status`}>
                                      {(field) => (
                                        <field.SelectField
                                          label="Status"
                                          options={missionCapabilityGapStatusOptions}
                                        />
                                      )}
                                    </form.AppField>
                                  </div>
                                </div>
                              </AccordionPrimitive.Content>
                            </AccordionPrimitive.Item>
                          </AccordionPrimitive.Root>
                        </div>
                      </div>
                    ))}

                    <Button
                      className="self-start"
                      onClick={() =>
                        gapArrayField.pushValue({
                          position: capabilityGaps.length,
                          priority: StudyMissionCapabilityGapPriority.MEDIUM,
                          question: '',
                          sourcePromptRef: null,
                          status: StudyMissionCapabilityGapStatus.OPEN,
                        })
                      }
                      type="button"
                      variant="link"
                    >
                      + Add Capability Gap
                    </Button>
                  </div>
                );
              }}
            </form.AppField>
          </form.AppForm>
        </form>
      </Modal>
      {mission && (
        <ConfirmDeleteModal
          isOpen={isConfirmingDelete}
          itemName="mission"
          onConfirm={handleConfirmDelete}
          setIsOpen={setIsConfirmingDelete}
        />
      )}
    </>
  );
};
